import type {
  PurposeItemData,
  PurposePreselection,
  PurposeSelection,
} from "../api/types";
import {
  DEFAULT_PURPOSE_PRESELECTION,
  PURPOSE_STATUS_INACTIVE,
} from "../constants";
import { productPurposeKey } from "../core/productMatrix";
import type {
  OpeningSelection,
  PurposeRow,
  RecordedLookup,
  SelectablePurpose,
  SelectionState,
} from "../types";
import { buildSelectionForMode, dataElementKey } from "./consentSelection";

export const resolvePurposePreselection = (
  raw: string | undefined | null
): PurposePreselection =>
  raw === "MANDATORY" || raw === "ALL" ? raw : DEFAULT_PURPOSE_PRESELECTION;

export const hasRecordedPurposeState = (
  selection: PurposeSelection | undefined
): selection is PurposeSelection => {
  if (!selection) {
    return false;
  }
  if (selection.status && selection.status !== PURPOSE_STATUS_INACTIVE) {
    return true;
  }
  return Object.keys(selection.data_elements ?? {}).length > 0;
};

export const buildPreselection = (
  purposes: ReadonlyArray<SelectablePurpose>,
  mode: PurposePreselection
): SelectionState => {
  if (mode === "ALL") {
    return buildSelectionForMode(
      purposes.map((purpose) => ({ purpose, productUuid: undefined })),
      "all",
      { selectedPurposes: {}, selectedDataElements: {} }
    );
  }

  const selectedPurposes: Record<string, boolean> = {};
  const selectedDataElements: Record<string, boolean> = {};

  purposes.forEach((purpose) => {
    let hasPreselectedElement = false;
    purpose.data_elements.forEach((element) => {
      const isPreselected =
        mode === "MANDATORY" && element.required && element.enabled;
      selectedDataElements[dataElementKey(purpose.uuid, element.uuid)] =
        isPreselected;
      hasPreselectedElement = hasPreselectedElement || isPreselected;
    });
    selectedPurposes[purpose.uuid] = hasPreselectedElement;
  });

  return { selectedPurposes, selectedDataElements };
};

export const openingSelection = (
  purposes: ReadonlyArray<PurposeItemData>,
  preselection: string | undefined | null,
  recordedFor: RecordedLookup
): OpeningSelection => {
  const hint = buildPreselection(
    purposes,
    resolvePurposePreselection(preselection)
  );
  const isRecorded = (purposeUuid: string, productUuid?: string) =>
    hasRecordedPurposeState(recordedFor(purposeUuid, productUuid));

  return {
    purpose: (purposeUuid, productUuid) =>
      !isRecorded(purposeUuid, productUuid) &&
      (hint.selectedPurposes[purposeUuid] ?? false),
    dataElement: (purposeUuid, elementUuid, productUuid) =>
      !isRecorded(purposeUuid, productUuid) &&
      (hint.selectedDataElements[dataElementKey(purposeUuid, elementUuid)] ??
        false),
  };
};

export const applyPreselection = (
  rows: ReadonlyArray<PurposeRow<PurposeItemData>>,
  preselection: string | undefined | null,
  recordedFor: RecordedLookup,
  seeded: SelectionState
): SelectionState => {
  const opening = openingSelection(
    rows.map((row) => row.purpose),
    preselection,
    recordedFor
  );
  const selectedPurposes = { ...seeded.selectedPurposes };
  const selectedDataElements = { ...seeded.selectedDataElements };

  rows.forEach(({ purpose, productUuid }) => {
    if (hasRecordedPurposeState(recordedFor(purpose.uuid, productUuid))) return;
    selectedPurposes[productPurposeKey(productUuid, purpose.uuid)] =
      opening.purpose(purpose.uuid, productUuid);
    purpose.data_elements.forEach((element) => {
      selectedDataElements[dataElementKey(purpose.uuid, element.uuid, productUuid)] =
        opening.dataElement(purpose.uuid, element.uuid, productUuid);
    });
  });

  return { selectedPurposes, selectedDataElements };
};

export const recordedAnswersFor = (
  rows: ReadonlyArray<PurposeRow>,
  recordedFor: RecordedLookup
): Record<string, boolean> =>
  rows.reduce((answers: Record<string, boolean>, { purpose, productUuid }) => {
    const selection = recordedFor(purpose.uuid, productUuid);
    if (hasRecordedPurposeState(selection)) {
      answers[productPurposeKey(productUuid, purpose.uuid)] =
        selection.selected === true;
    }
    return answers;
  }, {});

/**
 * The preselection hint on the keys the rows actually read. The hint is per
 * purpose, but a grouped notice keys its state per (product, purpose), so a
 * surface that seeds rows straight from `buildPreselection` writes keys its
 * own checkboxes and gate never read.
 */
export const preselectionForRows = (
  rows: ReadonlyArray<{ purpose: SelectablePurpose; productUuid?: string }>,
  preselection: SelectionState
): SelectionState => {
  const selectedPurposes: Record<string, boolean> = {};
  const selectedDataElements: Record<string, boolean> = {};

  rows.forEach(({ purpose, productUuid }) => {
    selectedPurposes[productPurposeKey(productUuid, purpose.uuid)] =
      preselection.selectedPurposes[purpose.uuid] ?? false;
    purpose.data_elements.forEach((element) => {
      selectedDataElements[dataElementKey(purpose.uuid, element.uuid, productUuid)] =
        preselection.selectedDataElements[dataElementKey(purpose.uuid, element.uuid)] ?? false;
    });
  });

  return { selectedPurposes, selectedDataElements };
};
